import { useEffect, useState } from 'react';
import Link from 'next/link';
import { Tab } from '@headlessui/react';
import clsx from 'clsx';
import { Container } from './Container';
import { getTranslation } from 'dictionary';
import { useUserContext } from 'context/UserContext';

export function PrimaryFeatures() {
  const { language } = useUserContext();
  const features = [
    {
      title: getTranslation('landingpage', 'CLOTHES', language),
      description: getTranslation('landingpage', 'CLOTHES_PHRASE', language),
      image: "images/screenshots/clothes.png",
      href: '/zoe',
    },
    {
      title: getTranslation('landingpage', 'BEAUTY', language),
      description: getTranslation('landingpage', 'BEAUTY_PHRASE', language),
      image: "images/screenshots/beauty.png",
      href: '/cosmetica-natural',
    },
    {
      title: getTranslation('landingpage', 'DECO', language),
      description: getTranslation('landingpage', 'DECO_PHRASE', language),
      image: "images/screenshots/deco.png",
      href: '/natura-art',
    },
    {
      title: getTranslation('landingpage', 'BAKERY', language),
      description: getTranslation('landingpage', 'BAKERY_PHRASE', language),
      image: "images/screenshots/bakery.png",
      href: '/dulcemania',
    },
  ];

  let [tabOrientation, setTabOrientation] = useState('horizontal');

  useEffect(() => {
    let lgMediaQuery = window.matchMedia('(min-width: 1024px)');

    function onMediaQueryChange({ matches }) {
      setTabOrientation(matches ? 'vertical' : 'horizontal');
    }

    onMediaQueryChange(lgMediaQuery);
    lgMediaQuery.addEventListener('change', onMediaQueryChange);

    return () => {
      lgMediaQuery.removeEventListener('change', onMediaQueryChange);
    };
  }, []);

  return (
    <section
      id="primary-features"
      aria-label="Features for running your books"
      className="relative overflow-hidden bg-blue-600 pt-20 pb-28 sm:py-32"
    >
      <img
        className="absolute top-1/2 left-1/2 max-w-none translate-x-[-44%] translate-y-[-42%]"
        src={'/images/background-features.jpg'}
        alt="background"
        width={1800}
        height={1088}
      />
      <Container className="relative">
        <div className="max-w-2xl md:mx-auto md:text-center xl:max-w-none">
          <h2 className="font-display text-center text-3xl tracking-tight text-white sm:text-4xl md:text-5xl">
            {getTranslation('landingpage', 'HOW_IT_WORK_TITLE', language)}
          </h2>
        </div>
        <div className="max-w-4xl mr-auto ml-auto">
          <Tab.Group
            as="div"
            className="mt-16 grid grid-cols-1 items-center gap-y-2 pt-10 sm:gap-y-6 md:mt-20 md:flex md:pt-0"
            vertical={tabOrientation === 'vertical'}
          >
            {({ selectedIndex }) => (
              <>
                <div className="-mx-4 flex overflow-x-auto pb-4 sm:mx-0 sm:overflow-visible sm:pb-0 ">
                  <Tab.List className="relative z-10 flex gap-x-4 whitespace-nowrap px-4 sm:mx-auto sm:px-0 md:mx-0 md:block md:gap-x-0 md:gap-y-1 md:whitespace-normal">
                    {features.map((feature, featureIndex) => (
                      <div
                        key={feature.title}
                        className={clsx(
                          'group relative rounded-full py-1 px-4 md:rounded-r-none md:rounded-l-xl md:p-6',
                          selectedIndex === featureIndex
                            ? 'bg-white md:bg-white/10 md:ring-1 md:ring-inset md:ring-white/10'
                            : 'hover:bg-white/10 md:hover:bg-white/5'
                        )}
                      >
                        <h3>
                          <Tab
                            className={clsx(
                              'font-display text-lg focus:outline-none',
                              selectedIndex === featureIndex
                                ? 'text-blue-600 md:text-white'
                                : 'text-blue-100 hover:text-white md:text-white'
                            )}
                          >
                            <span className="absolute inset-0 rounded-full md:rounded-r-none md:rounded-l-xl" />
                            {feature.title}
                          </Tab>
                        </h3>
                        <p
                          className={clsx(
                            'mt-2 hidden text-sm md:block font-light text-center md:text-left',
                            selectedIndex === featureIndex
                              ? 'text-white'
                              : 'text-blue-100 group-hover:text-white'
                          )}
                        >
                          {feature.description}
                        </p>
                      </div>
                    ))}
                  </Tab.List>
                </div>
                <Tab.Panels className="">
                  {features.map((feature) => (
                    <Tab.Panel key={feature.title} unmount={false}>
                      <div className="relative sm:px-6 md:hidden">
                        <div className="absolute -inset-x-4 top-[-6.5rem] bottom-[-4.25rem] bg-white/10 ring-1 ring-inset ring-white/10 " />
                        <p className="relative mx-auto max-w-2xl text-base text-white text-center">
                          {feature.description}
                        </p>
                      </div>
                      <div className="z-20 flex justify-center items-center md:justify-start">
                        <Link
                          key={feature.title}
                          href={feature.href}
                          target="_blank"
                          rel="noreferrer"
                          className="mt-10 max-w-[400px] md:w-[350px] overflow-hidden rounded-xl bg-slate-50 shadow-xl shadow-blue-900/20 sm:w-auto md:mt-0 "
                        >
                          <img
                            className="w-full z-20"
                            src={feature.image}
                            alt={feature.title}
                            height={702}
                            width={350}
                          />
                        </Link>
                      </div>
                    </Tab.Panel>
                  ))}
                </Tab.Panels>
              </>
            )}
          </Tab.Group>
        </div>
      </Container>
    </section>
  );
}
